<!-- Chat form question control for entering and submitting a birth date. -->

<script lang="ts" setup>
import { Form } from '@nzyme/vue-forms/components/Form.jsx';
import { useForm } from '@nzyme/vue-forms/useForm.js';
import { useFormFields } from '@nzyme/vue-forms/useFormFields.js';
import { maxDateValidator } from '@nzyme/vue-forms/validators/maxDateValidator.js';
import { requiredValidator } from '@nzyme/vue-forms/validators/requiredValidator.js';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { useEmitAsync } from '@nzyme/vue-utils/useEmitAsync.js';
import { computed } from 'vue';

import type { BirthDateInputConfig, BirthDateInputValue } from '@sigma/common/forms/BirthDateInput.js';
import { formatDateIso } from '@sigma/utils/format/formatDateIso.js';

import Button from '../../Button.vue';
import DateInput from '../../forms/DateInput.vue';
import * as l from './ChatBirthDateInput.loc.js';

interface Props {
  config: BirthDateInputConfig;
}

interface Emits {
  submit: [value: BirthDateInputValue];
}

defineProps<Props>();
defineEmits<Emits>();

const t = useTranslate();
const emitAsync = useEmitAsync<Emits>();

const maxDate = computed(() => new Date());

const form = useForm({
  date: null as Date | null,
});

const fields = useFormFields(form, {
  date: [
    requiredValidator({ message: () => t(l.enterBirthDate) }),
    maxDateValidator({ maxDate, message: () => t(l.birthDateInFuture) }),
  ],
});

async function handleSubmit() {
  const valid = await form.validate();
  if (!valid) {
    return;
  }

  await emitAsync('submit', {
    type: 'BIRTH_DATE',
    date: formatDateIso(form.value.date!),
  });
}
</script>

<template>
  <Form
    :class="css.formContainer"
    @submit="handleSubmit"
  >
    <DateInput
      :field="fields.date"
      :max="maxDate"
    />

    <Button
      tid="chat-question-submit"
      variant="primary"
      size="large"
      color="black"
      rounded
      :class="css.submitButton"
      type="submit"
    >
      {{ t(l.submitButton) }}
    </Button>
  </Form>
</template>

<style lang="scss" module="css">
.formContainer {
  display: flex;
  flex-direction: column;
  gap: $spacing-08;
}

.submitButton {
  margin-top: $spacing-08;
}
</style>
